import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Mic, MicOff, Phone, PhoneOff, Radio, Send } from 'lucide-react'
import { toast } from 'sonner'

import {
  useAgentCallSay,
  useAgentCallHangup,
} from '@/hooks/useAdmin'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { adminApiService } from '@/services/adminApi'
import type { AgentCallEvent } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChatContainerRoot,
  ChatContainerContent,
  ChatContainerScrollAnchor,
} from '@/components/ui/chat-container'
import { Message, MessageAvatar, MessageContent } from '@/components/ui/message'
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputActions,
  PromptInputAction,
} from '@/components/ui/prompt-input'

// --- Types ---

interface ConsoleMessage {
  type: 'speech' | 'say' | 'system'
  text: string
  variant?: 'default' | 'success' | 'warning' | 'destructive' | 'muted'
  timestamp: string
}

// --- Hooks ---

function getWsUrl(wsPath: string): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL || ''
  return `${apiBase.replace(/^http/, 'ws')}${wsPath}`
}

function decodePcm16(base64: string): Float32Array {
  const raw = atob(base64)
  const float32 = new Float32Array(raw.length / 2)
  for (let i = 0; i < float32.length; i++) {
    float32[i] = (raw.charCodeAt(i * 2) | (raw.charCodeAt(i * 2 + 1) << 8)) << 16 >> 16
  }
  for (let i = 0; i < float32.length; i++) {
    float32[i] /= 32768
  }
  return float32
}

function encodePcm16(float32: Float32Array): string {
  const int16 = new Int16Array(float32.length)
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]))
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  const bytes = new Uint8Array(int16.buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function useBrowserAudio(wsPath: string | null, isActive: boolean) {
  const wsRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const captureCtxRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const playbackCtxRef = useRef<AudioContext | null>(null)
  const sourcesRef = useRef<AudioBufferSourceNode[]>([])
  const nextPlayTimeRef = useRef(0)
  const [isMuted, setIsMuted] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)

  const stop = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event: 'stop' }))
      wsRef.current.close()
    }
    wsRef.current = null

    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null

    processorRef.current?.disconnect()
    processorRef.current = null

    captureCtxRef.current?.close()
    captureCtxRef.current = null

    playbackCtxRef.current?.close()
    playbackCtxRef.current = null

    sourcesRef.current = []
    nextPlayTimeRef.current = 0
  }, [])

  const start = useCallback(async () => {
    if (!wsPath) return
    setMicError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
      })
      streamRef.current = stream

      // Capture
      const captureCtx = new AudioContext({ sampleRate: 16000 })
      captureCtxRef.current = captureCtx
      const source = captureCtx.createMediaStreamSource(stream)
      const processor = captureCtx.createScriptProcessor(4096, 1, 1)
      processorRef.current = processor

      // Playback
      const playbackCtx = new AudioContext({ sampleRate: 16000 })
      playbackCtxRef.current = playbackCtx

      // WebSocket
      const ws = new WebSocket(getWsUrl(wsPath))
      wsRef.current = ws

      ws.onopen = () => ws.send(JSON.stringify({ event: 'start' }))

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          if (msg.event === 'media' && msg.media?.payload) {
            const samples = decodePcm16(msg.media.payload)
            const buffer = playbackCtx.createBuffer(1, samples.length, 16000)
            buffer.getChannelData(0).set(samples)

            const bufferSource = playbackCtx.createBufferSource()
            bufferSource.buffer = buffer
            bufferSource.connect(playbackCtx.destination)
            sourcesRef.current.push(bufferSource)
            bufferSource.onended = () => {
              const idx = sourcesRef.current.indexOf(bufferSource)
              if (idx > -1) sourcesRef.current.splice(idx, 1)
            }

            const now = playbackCtx.currentTime
            if (nextPlayTimeRef.current < now) nextPlayTimeRef.current = now
            bufferSource.start(nextPlayTimeRef.current)
            nextPlayTimeRef.current += buffer.duration
          } else if (msg.event === 'clear') {
            sourcesRef.current.forEach(s => { try { s.stop() } catch {} })
            sourcesRef.current = []
            nextPlayTimeRef.current = playbackCtx.currentTime
          }
        } catch {}
      }

      ws.onclose = () => stop()

      processor.onaudioprocess = (e) => {
        if (ws.readyState !== WebSocket.OPEN) return
        const payload = encodePcm16(e.inputBuffer.getChannelData(0))
        ws.send(JSON.stringify({ event: 'media', media: { payload } }))
      }

      source.connect(processor)
      processor.connect(captureCtx.destination)
    } catch (err) {
      setMicError(err instanceof Error ? err.message : 'Microphone access denied')
    }
  }, [wsPath, stop])

  const toggleMute = useCallback(() => {
    const track = streamRef.current?.getAudioTracks()[0]
    if (track) {
      track.enabled = !track.enabled
      setIsMuted(!track.enabled)
    }
  }, [])

  // Auto start/stop
  const startedRef = useRef<string | null>(null)
  useEffect(() => {
    if (isActive && wsPath && startedRef.current !== wsPath) {
      startedRef.current = wsPath
      start()
    } else if (!isActive) {
      startedRef.current = null
      stop()
    }
  }, [isActive, wsPath, start, stop])

  useEffect(() => stop, [stop])

  return { stop, toggleMute, isMuted, micError }
}

function useEventPolling(
  agentCallId: string,
  isActive: boolean,
  initialSequence: number,
  onEvents: (events: AgentCallEvent[]) => void,
) {
  const sequenceRef = useRef(initialSequence)
  const cancelledRef = useRef(false)

  // Keep sequence in sync if initial events arrive after mount
  useEffect(() => {
    if (initialSequence > sequenceRef.current) {
      sequenceRef.current = initialSequence
    }
  }, [initialSequence])

  useEffect(() => {
    if (!isActive) return

    cancelledRef.current = false

    const poll = async () => {
      while (!cancelledRef.current) {
        try {
          const { events } = await adminApiService.pollAgentCallEvents(agentCallId, sequenceRef.current, 25)
          if (cancelledRef.current) break

          if (events?.length) {
            onEvents(events)
            sequenceRef.current = Math.max(...events.map(e => e.sequence))
            if (events.some(e => e.eventType === 'call_ended')) break
          }
        } catch {
          if (cancelledRef.current) break
          await new Promise(r => setTimeout(r, 2000))
        }
      }
    }

    poll()
    return () => { cancelledRef.current = true }
  }, [agentCallId, isActive, onEvents])
}

// --- Event → Message mapping ---

function eventToMessage(event: AgentCallEvent): ConsoleMessage | null {
  const ts = event.createdAt
  const d = event.data as Record<string, unknown> | null

  switch (event.eventType) {
    case 'call_ringing':
      return { type: 'system', text: 'Ringing...', variant: 'muted', timestamp: ts }
    case 'call_connected':
      return { type: 'system', text: 'Connected', variant: 'success', timestamp: ts }
    case 'speech':
      return { type: 'speech', text: (d?.text as string) || '[inaudible]', timestamp: ts }
    case 'speech_interrupted':
      return { type: 'system', text: 'Callee interrupted', variant: 'warning', timestamp: ts }
    case 'call_ended':
      return { type: 'system', text: `Call ended: ${(d?.reason as string) || 'unknown'}`, variant: 'destructive', timestamp: ts }
    case 'error':
      return { type: 'system', text: `Error: ${(d?.message as string) || 'unknown'}`, variant: 'destructive', timestamp: ts }
    default:
      return { type: 'system', text: event.eventType, variant: 'muted', timestamp: ts }
  }
}

// --- Components ---

function StatusBadge({ status, callMode }: { status: string; callMode: 'browser' | 'phone' }) {
  const dotClass: Record<string, string> = {
    initiating: 'bg-blue-500 animate-pulse',
    ringing: 'bg-yellow-500 animate-pulse',
    connected: 'bg-green-500 animate-pulse',
    ended: 'bg-gray-400',
    failed: 'bg-red-500',
  }

  return (
    <div className="flex items-center gap-4">
      <Badge variant="outline">
        {callMode === 'browser' ? (
          <><Radio className="h-3 w-3 mr-1" /> Browser</>
        ) : (
          <><Phone className="h-3 w-3 mr-1" /> Phone</>
        )}
      </Badge>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass[status] || 'bg-gray-400'}`} />
        <span className="font-medium capitalize">{status}</span>
      </div>
    </div>
  )
}

function SystemEvent({ text, variant = 'default' }: { text: string; variant?: string }) {
  const classes: Record<string, string> = {
    success: 'bg-green-500/10 text-green-600 border-green-500/20',
    warning: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    destructive: 'bg-red-500/10 text-red-600 border-red-500/20',
    muted: 'bg-muted/50 text-muted-foreground border-border',
    default: 'bg-muted/50 text-muted-foreground border-border',
  }

  return (
    <div className="flex justify-center py-1">
      <span className={`text-xs px-3 py-1 rounded-full border ${classes[variant] || classes.default}`}>
        {text}
      </span>
    </div>
  )
}

// --- Page ---

export const Route = createFileRoute('/admin/agent-console/$callId')({
  component: AgentConsolePage,
})

function AgentConsolePage() {
  const { callId } = Route.useParams()
  const navigate = useNavigate()
  const { data: callData, isLoading } = useQuery(adminQueries.agentCall(callId))

  const [messages, setMessages] = useState<ConsoleMessage[]>([])
  const [sayText, setSayText] = useState('')
  const [callStatus, setCallStatus] = useState('initiating')
  const [initialSequence, setInitialSequence] = useState(0)
  const seededRef = useRef(false)

  const sayMutation = useAgentCallSay()
  const hangupMutation = useAgentCallHangup()

  const isActive = ['initiating', 'ringing', 'connected'].includes(callStatus)
  const isReady = seededRef.current
  const callMode = callData?.provider === 'browser' ? 'browser' as const : 'phone' as const
  const wsPath = callData?.wsPath || null

  const browserAudio = useBrowserAudio(wsPath, isActive && callMode === 'browser')

  // Seed from initial fetch (once)
  useEffect(() => {
    if (!callData || seededRef.current) return
    seededRef.current = true

    setCallStatus(callData.status)

    if (callData.events?.length) {
      const msgs = callData.events.map(eventToMessage).filter(Boolean) as ConsoleMessage[]
      setMessages(msgs)
      setInitialSequence(Math.max(...callData.events.map(e => e.sequence)))
    }
  }, [callData])

  const handleEvents = useCallback((events: AgentCallEvent[]) => {
    const newMessages: ConsoleMessage[] = []

    for (const event of events) {
      if (event.eventType === 'call_ringing') setCallStatus('ringing')
      else if (event.eventType === 'call_connected') setCallStatus('connected')
      else if (event.eventType === 'call_ended') {
        setCallStatus('ended')
        toast.info(`Call ended: ${(event.data?.reason as string) || 'unknown'}`)
      }

      const msg = eventToMessage(event)
      if (msg) newMessages.push(msg)
    }

    if (newMessages.length > 0) {
      setMessages(prev => [...prev, ...newMessages])
    }
  }, [])

  useEventPolling(callId, isActive && isReady, initialSequence, handleEvents)

  const handleSay = () => {
    const text = sayText.trim()
    if (!text) return
    setSayText('')
    setMessages(prev => [...prev, { type: 'say', text, timestamp: new Date().toISOString() }])
    sayMutation.mutate({ id: callId, text })
  }

  const handleHangup = () => {
    browserAudio.stop()
    hangupMutation.mutate(callId)
  }

  if (isLoading) {
    return (
      <div className="-m-6 h-screen flex flex-col">
        <div className="border-b p-4"><Skeleton className="h-8 w-64" /></div>
        <div className="flex-1 p-4"><Skeleton className="h-full w-full" /></div>
      </div>
    )
  }

  return (
    <div className="-m-6 h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b px-6 py-4 flex items-center justify-between bg-card shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/admin/calls' })}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Agent Console</h1>
            <p className="text-xs text-muted-foreground font-mono">{callId}</p>
          </div>
        </div>
        <StatusBadge status={callStatus} callMode={callMode} />
      </header>

      {/* Messages */}
      <main className="flex-1 flex flex-col min-h-0 p-4">
        <ChatContainerRoot className="flex-1">
          <ChatContainerContent className="space-y-3 px-4">
            {messages.length === 0 && (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                {isActive ? 'Waiting for events...' : 'No events recorded.'}
              </div>
            )}
            {messages.map((msg, i) => {
              if (msg.type === 'system') {
                return <SystemEvent key={i} text={msg.text} variant={msg.variant} />
              }

              const isSay = msg.type === 'say'
              return (
                <Message key={i} className={isSay ? 'justify-end' : 'justify-start'}>
                  {!isSay && <MessageAvatar src="" alt="Callee" fallback="C" className="bg-muted" />}
                  <div className={`flex flex-col gap-0.5 max-w-[80%] ${isSay ? 'items-end' : ''}`}>
                    <MessageContent className={isSay ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}>
                      <p className="text-sm">{msg.text}</p>
                    </MessageContent>
                    <span className={`text-xs text-muted-foreground ${isSay ? 'pr-2' : 'pl-2'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  {isSay && <MessageAvatar src="" alt="Agent" fallback="A" className="bg-primary/20" />}
                </Message>
              )
            })}
            <ChatContainerScrollAnchor />
          </ChatContainerContent>
        </ChatContainerRoot>

        {/* Controls */}
        <div className="border-t pt-3 mt-3 shrink-0">
          {callMode === 'browser' && isActive && (
            <div className="flex items-center gap-2 px-1 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={browserAudio.toggleMute}
                disabled={callStatus !== 'connected'}
                className={browserAudio.isMuted ? 'text-red-500' : ''}
              >
                {browserAudio.isMuted ? <><MicOff className="h-4 w-4 mr-2" /> Unmute</> : <><Mic className="h-4 w-4 mr-2" /> Mute</>}
              </Button>
              {browserAudio.micError && <span className="text-sm text-destructive">{browserAudio.micError}</span>}
              {!browserAudio.micError && callStatus === 'connected' && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Audio active
                </span>
              )}
            </div>
          )}

          {isActive ? (
            <div className="flex gap-2 items-end">
              <PromptInput
                value={sayText}
                onValueChange={setSayText}
                onSubmit={handleSay}
                disabled={callStatus !== 'connected'}
                className="flex-1"
              >
                <PromptInputTextarea placeholder="Type a message to say..." />
                <PromptInputActions className="justify-end px-2 pb-2">
                  <PromptInputAction tooltip="Send message">
                    <Button
                      size="sm"
                      onClick={handleSay}
                      disabled={!sayText.trim() || sayMutation.isPending || callStatus !== 'connected'}
                      className="rounded-full h-8 w-8 p-0"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </PromptInputAction>
                </PromptInputActions>
              </PromptInput>
              <Button
                variant="destructive"
                size="lg"
                onClick={handleHangup}
                disabled={hangupMutation.isPending}
                className="rounded-full h-12 w-12 p-0 shrink-0"
              >
                <PhoneOff className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end px-1">
              <Button onClick={() => navigate({ to: '/admin/calls' })}>
                Back to Calls
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default AgentConsolePage
